import * as React from 'react';
import { State } from '~/Reducers';
import { getStepByName, GUIDE_NAMES, IGuideStep, PLACEMENT } from '~/components/WelcomePage/steps';
import { useDispatch, useSelector } from 'react-redux';
import equal from 'fast-deep-equal/react';
import Guide from '~/components/WelcomePage/Guide';
import { hideGuideStep } from '~/Actions/ActionAccount';
import { IDevModeParams } from '~/Actions/ActionAccountType';
import { isMobileOrTabletWindow } from '~/utils/utils';
import { ICurrentPage } from '~/Actions/ActionAppType';
import { CategoryType } from '~/types/category';

interface IGuideDecorator {
    children: React.ReactChild | React.ReactChild[];
    names: GUIDE_NAMES[];
    className?: string;
    onClick?: (event: React.MouseEvent) => void;
    placement?: PLACEMENT;
}

interface IStateSelector {
    categories: ICategories;
    currentPage: ICurrentPage;
    activeGuideName: GUIDE_NAMES;
    steps: IGuideStep[];
    popupActive: IPopup;
    port: IPort;
    infoScreenIsVisible: boolean;
    currentNotificationVisibleName: string;
    devMode: IDevModeParams;
    isFinishedRequest: boolean;
    isFullscreen: boolean;
}

const stateSelector = (state: State): IStateSelector => {
    return {
        activeGuideName: state.ReducerApp.guide?.name,
        steps: state.ReducerAccount?.guideSteps,
        popupActive: state.ReducerApp.popupActive,
        port: state.ReducerApp.port,
        infoScreenIsVisible: state.ReducerApp.infoScreen?.isVisible,
        currentNotificationVisibleName: state.ReducerApp.currentNotificationVisibleName,
        devMode: state.ReducerApp.devMode,
        isFinishedRequest: state.ReducerApp.isFinishedRequest,
        isFullscreen: state.ReducerApp.isFullscreen,
        categories: state.ReducerApp.categories,
        currentPage: state.ReducerApp.currentPage,
    };
};

function isNeedToHiddenForSpecialCategories(type: CategoryType) {
    return [CategoryType.ENROTH].includes(type);
}

function isAllowedInFullscreenCategory(type: CategoryType, step: IGuideStep) {
    if (CategoryType.SALVAGE === type && step && [GUIDE_NAMES.guide_salvage_resources].includes(step.name)) {
        return true;
    }

    return false;
}

const GuideDecorator = ({ children, names, className, onClick, placement }: IGuideDecorator) => {
    const dispatch = useDispatch();
    const state = useSelector<State, IStateSelector>(stateSelector, equal);
    const [isStarted, setStart] = React.useState<boolean>(false);
    const [target, setTarget] = React.useState<HTMLDivElement>(null);
    const ref = React.useRef<HTMLDivElement>(null);
    const timingRef = React.useRef<{ delay: NodeJS.Timeout; duration: NodeJS.Timeout }>({ delay: null, duration: null });

    const getStep = () => {
        if (!Array.isArray(names)) {
            return null;
        }

        if (names.includes(state.steps?.[0]?.name)) {
            return getStepByName(state.steps[0]?.name);
        }

        const stepForCurrentCategory = state.steps?.find((step) => step.category === state.categories[state.currentPage?.name]?.type);
        if (stepForCurrentCategory && names.includes(stepForCurrentCategory.name)) {
            return getStepByName(stepForCurrentCategory.name);
        }

        return null;
    };
    const step = getStep();

    const isSpecialCategory = isNeedToHiddenForSpecialCategories(state.categories[state.currentPage?.name]?.type);
    const isNeedToHiddenInFullscreen = state.isFullscreen && !isAllowedInFullscreenCategory(state.categories[state.currentPage?.name]?.type, step);
    const isNeedToHidden = isSpecialCategory || isNeedToHiddenInFullscreen || !!state.popupActive || state.port?.isVisible || state.infoScreenIsVisible || state.currentNotificationVisibleName;

    const setRef = (_ref: HTMLDivElement) => {
        if (_ref) {
            ref.current = _ref;
            setTarget(ref.current);
        }
    };

    const finish = () => dispatch(hideGuideStep(step));

    const stop = () => {
        if (!isStarted) return;
        setStart(false);
        step.afterShown?.();
        finish();
    };

    const _onClick = (event: React.MouseEvent) => {
        stop();
        onClick?.(event);
    };

    const show = () => {
        if (isStarted) {
            return;
        }

        timingRef.current.delay = setTimeout(
            () => {
                setStart(true);
                if (step.duration) {
                    timingRef.current.duration = setTimeout(stop, step.duration * 1000);
                }
            },
            (step.delay || 1) * 1000,
        );
    };

    const showIfNeeded = () => {
        if (getStep()) {
            show();
        }
    };

    const clearTimings = () => {
        clearTimeout(timingRef.current.delay);
        clearTimeout(timingRef.current.duration);
    };

    React.useEffect(() => {
        if (!Array.isArray(names) || !state.steps?.length || !ref.current) {
            return;
        }
        if (!isNeedToHidden) {
            showIfNeeded();
        }
    }, [state.steps]);

    React.useEffect(() => {
        if (isNeedToHidden || !state.isFinishedRequest) {
            setStart(false);
            clearTimings();
        } else {
            showIfNeeded();
        }
    }, [isNeedToHidden, state.isFinishedRequest]);

    React.useEffect(() => {
        return clearTimings;
    }, []);

    if (!Array.isArray(names) || !step || state.devMode?.disableWelcomePage) {
        return (
            <div className={className} onClick={onClick}>
                {children}
            </div>
        );
    }

    return (
        <React.Fragment>
            <div ref={setRef} className={className} onClick={_onClick}>
                {children}
            </div>
            {!isMobileOrTabletWindow && isStarted && target && !isNeedToHidden && <Guide step={step} run={isStarted} onFinishGuide={stop} target={target} placement={placement} />}
        </React.Fragment>
    );
};

export default GuideDecorator;
